import React, { useState, useEffect, useRef } from 'react';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';
import { Loader2, Mail, Phone, Linkedin, AlertCircle, Lightbulb, Calendar } from 'lucide-react';
import LinkedInSubtypeBadge from '@/components/common/LinkedInSubtypeBadge';
import OutreachEngagementIcons from '@/components/common/OutreachEngagementIcons';
import {
  canUseAnalyticsV2AllOutreach,
  getChannelDisplayLabel,
  getOutboxDraftHeadline,
  isCallOutboxChannel,
  isEmailOutboxChannel,
  isLinkedInOutboxChannel,
} from '@/lib/outboxChannel';
import { sanitizeId } from '@/components/utils/id';
import { getTouchTimelineDisplay, isCurrentlyEngaged } from '@/lib/personPlaybookDisplay';

const API_BASE_URL = (() => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return 'https://api.neogtm.com';
  return 'https://api.neogtm.com';
})();

const CHANNEL_ICONS = { email: Mail, call: Phone, linkedin: Linkedin, recommendation: Lightbulb };
const TIMELINE_STATUS_EMOJI = { sent: '✅', replied: '💬', bounced: '❌', scheduled: '🔵', draft_ready: '📝', needs_review: '⚠️ ', pending: '○', cancelled: '—' };

function getTouchScheduledRaw(touch, enrolledAt) {
  if (!touch || typeof touch !== 'object') return null;
  const direct = touch.scheduled_send_at ?? touch.scheduledSendAt ?? touch.scheduled_for ?? touch.scheduled_at ?? touch.due_at ?? touch.dueAt ?? touch.send_at ?? touch.planned_send_at ?? null;
  if (direct != null) { const s = String(direct).trim(); if (s && s !== 'null') return s; }
  const offset = touch.day_offset ?? touch.dayOffset ?? touch.delay_days;
  if (enrolledAt != null && offset != null && Number.isFinite(Number(offset))) {
    const base = new Date(enrolledAt);
    if (Number.isFinite(base.getTime())) { const d = new Date(base); d.setDate(d.getDate() + Number(offset)); return d.toISOString(); }
  }
  return null;
}

function hasRecentOutreachEngagement(messages, days = 30) {
  const cutoff = Date.now() - days * 86400000;
  return (messages || []).some((m) => {
    for (const field of ['replied_at', 'clicked_at', 'opened_at']) {
      const raw = m?.[field]; if (!raw) continue;
      const ts = new Date(raw).getTime();
      if (Number.isFinite(ts) && ts >= cutoff) return true;
    }
    return false;
  });
}

const OutreachMessageList = ({ messages, formatDate }) => {
  if (!messages || messages.length === 0) return null;
  return (
    <div className="mt-6">
      <h3 className="font-semibold text-[#0A1A2F] mb-3">All Outreach ({messages.length})</h3>
      <div className="space-y-2">
        {messages.map((message) => {
          const messageKey = message.id ?? `${message.channel || 'unknown'}-${message.completed_at || ''}-${message.subject || ''}`;
          const Icon = isLinkedInOutboxChannel({ channel: message.channel }) ? Linkedin : CHANNEL_ICONS[message.channel] || Mail;
          const iconColor = isEmailOutboxChannel({ channel: message.channel }) ? 'text-blue-600' : isLinkedInOutboxChannel({ channel: message.channel }) ? 'text-[#0077B5]' : isCallOutboxChannel({ channel: message.channel }) ? 'text-green-600' : message.channel === 'recommendation' ? 'text-purple-600' : 'text-blue-600';
          const sourceLabel = message.source === 'playbook' ? message.playbook_name || 'Playbook' : message.source === 'diy_playbook' ? message.playbook_name || 'DIY Playbook' : message.source === 'profile_outreach' ? 'Profile Outreach' : message.source === 'today_plan' ? "Today's Plan" : message.source === 'recommendation' ? 'NEO Recommendation' : 'Manual';
          return (
            <div key={messageKey} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A1A2F] truncate">{message.subject || 'Completed task'}</p>
                    <p className="text-xs text-[#5C6B7A] mt-0.5">{formatDate(message.completed_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <OutreachEngagementIcons
                    openedAt={message.opened_at}
                    clickedAt={message.clicked_at}
                    repliedAt={message.replied_at}
                  />
                  <Badge variant="outline" className="text-xs">{sourceLabel}</Badge>
                  <Badge className="bg-gray-100 text-gray-700 text-xs">{getChannelDisplayLabel({ channel: message.channel })}</Badge>
                  <LinkedInSubtypeBadge source={{ channel: message.channel }} debugContext="playbooks-tab-message" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PlaybookCard = ({ pb, formatDate }) => {
  const touches = Array.isArray(pb.touches) ? pb.touches : [];
  const total = pb.total_touches ?? touches.length ?? 0;
  const delivered = pb.delivered_touches ?? touches.filter((t) => t.sent_at).length;
  const progressPercent = total > 0 ? Math.min(100, (delivered / total) * 100) : 0;
  const isEventPB = Boolean(pb.event_id || pb.event_name);
  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-lg text-[#0A1A2F]">{pb.playbook_name}</CardTitle>
          {isEventPB && (
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
              <Calendar className="w-3 h-3 mr-1 inline" />
              Event {pb.event_name ? `· ${pb.event_name}` : ''}
            </Badge>
          )}
        </div>
        <div className="text-sm text-[#5C6B7A] mt-1">📅 Enrolled: {formatDate(pb.enrolled_at)}</div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-[#0A1A2F]">Delivered: {delivered} · Planned: {total}</span>
            <span className="text-[#5C6B7A]">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#0A1A2F] mb-3">Timeline:</p>
          <div className="space-y-2">
            {touches.slice(0, 8).map((touch) => {
              const Icon = isLinkedInOutboxChannel({ channel: touch.channel }) ? Linkedin : CHANNEL_ICONS[touch.channel] || Mail;
              const scheduledAt = getTouchScheduledRaw(touch, pb.enrolled_at);
              const display = getTouchTimelineDisplay(touch, { scheduledAt });
              const emoji = TIMELINE_STATUS_EMOJI[display.status] || '○';
              return (
                <div key={`${touch.stage_no}-${touch.channel || 'unknown'}`} className={`flex items-center gap-2 text-sm flex-wrap ${display.strikethrough ? 'line-through opacity-70' : ''}`}>
                  <span className="text-lg">{emoji}</span>
                  {Icon && <Icon className="w-4 h-4 text-[#5C6B7A]" />}
                  <span className="text-[#0A1A2F]">Touch {touch.stage_no}</span>
                  <LinkedInSubtypeBadge source={{ channel: touch.channel, allSteps: touches, touch }} debugContext="playbooks-tab-touch" />
                  <span className="text-[#5C6B7A]">— {getChannelDisplayLabel({ channel: touch.channel, allSteps: touches, touch })}</span>
                  <span className={display.className}>{display.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PlaybooksTab({ personId, currentTenant: currentTenantProp, onDataLoaded }) {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, '');

  const [allPlaybooks, setAllPlaybooks] = useState([]);
  const [scheduledUpcoming, setScheduledUpcoming] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [oneOffMessages, setOneOffMessages] = useState([]);
  const [engagementMetrics, setEngagementMetrics] = useState(null);

  useEffect(() => {
    // [2026-07-01] Do NOT fire onDataLoaded(0) on mount — that caused a
    // brief "Interactions (0)" flash whenever the tab mounted, because
    // the parent's pre-fetched real count got overwritten with 0. The
    // count effect below fires onDataLoaded with the real number once
    // fetches resolve; between mount and first fetch, the parent's
    // pre-fetched value stays intact.
    loadAllPlaybooks(); loadOneOffMessages(); loadEngagementMetrics();
    const interval = setInterval(() => { loadAllPlaybooks(); loadOneOffMessages(); loadEngagementMetrics(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, currentTenant, contextUser?.id]);

  const onDataLoadedRef = useRef(onDataLoaded);
  useEffect(() => { onDataLoadedRef.current = onDataLoaded; });
  useEffect(() => {
    const deliveredTouches = allPlaybooks.reduce((acc, pb) => acc + Number(pb?.delivered_touches || 0), 0);
    onDataLoadedRef.current?.(deliveredTouches + oneOffMessages.length);
  }, [allPlaybooks, oneOffMessages]);

  const loadAllPlaybooks = async () => {
    if (!personId || !contextUser || !tenantId) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const userParam = contextUser?.id ? `&user_id=${encodeURIComponent(contextUser.id)}` : '';
      const response = await fetch(`${API_BASE_URL}/api/personas/${personId}/all-playbooks?tenant_id=${tenantId}${userParam}`, { credentials: 'include' });
      if (!response.ok) {
        if (response.status === 404) { setApiAvailable(false); setApiError(null); setAllPlaybooks([]); setScheduledUpcoming([]); return; }
        throw new Error(`API error: ${response.status}`);
      }
      const result = await response.json();
      setAllPlaybooks(Array.isArray(result?.playbooks) ? result.playbooks : []);
      setScheduledUpcoming(Array.isArray(result?.scheduled_upcoming) ? result.scheduled_upcoming : []);
      setApiAvailable(true); setApiError(null);
    } catch (err) {
      console.log('All-playbooks API error:', err.message);
      setApiError(err?.message || 'Could not load playbook data.');
    } finally { setIsLoading(false); }
  };

  const loadEngagementMetrics = async () => {
    if (!personId || !contextUser || !tenantId) return;
    try {
      const params = new URLSearchParams({ tenant_id: tenantId, days: '30', min_opens: '0', min_clicks: '0', limit: '500' });
      if (contextUser?.id) params.append('user_id', contextUser.id);
      params.append('person_id', personId);
      const response = await fetch(`${API_BASE_URL}/api/email-analytics/personas-engagement?${params.toString()}`, { credentials: 'include' });
      if (!response.ok) { setEngagementMetrics(null); return; }
      const result = await response.json();
      const rows = Array.isArray(result?.personas) ? result.personas : [];
      const targetId = sanitizeId(personId);
      const match = rows.find((row) => sanitizeId(row?.person_id ?? row?.persona_id) === targetId) || null;
      setEngagementMetrics(match);
    } catch { setEngagementMetrics(null); }
  };

  const loadOneOffMessages = async () => {
    if (!personId || !contextUser || !tenantId) return;
    if (!canUseAnalyticsV2AllOutreach(personId)) { setOneOffMessages([]); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics-v2/personas/${encodeURIComponent(personId)}/all-outreach?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(contextUser?.id || '')}`, { credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        const rawRows = Array.isArray(result) ? result : Array.isArray(result?.rows) ? result.rows : Array.isArray(result?.messages) ? result.messages : Array.isArray(result?.data) ? result.data : [];
        const formattedMessages = rawRows.map((msg) => ({ id: msg.id, step_id: msg.step_id, subject: msg.subject || 'Manual completion', channel: msg.channel || 'email', touch_number: msg.stage_no || 1, completed_at: msg.sent_at, source: msg.source || 'manual', playbook_name: msg.playbook_name, status: msg.status || 'sent', opened_at: msg.opened_at, clicked_at: msg.clicked_at, replied_at: msg.replied_at }));
        setOneOffMessages(formattedMessages);
      }
    } catch (err) { console.log('All outreach messages not available:', err.message); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try { const d = new Date(dateStr); if (!Number.isFinite(d.getTime())) return 'N/A'; return format(d, 'MMM d, yyyy'); } catch { return 'N/A'; }
  };

  const showCurrentlyEngaged = isCurrentlyEngaged(engagementMetrics) || hasRecentOutreachEngagement(oneOffMessages);

  if (isLoading && allPlaybooks.length === 0) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" /></div>;
  }
  if (apiError) {
    return (
      <div className="space-y-6">
        <OutreachMessageList messages={oneOffMessages} formatDate={formatDate} />
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-300 mb-4" />
          <p className="text-lg font-medium text-[#0A1A2F] mb-2">Couldn&apos;t load playbook data</p>
          <p className="text-sm text-[#5C6B7A] max-w-md">We hit an error fetching this persona&apos;s playbook state. Try refreshing.</p>
        </div>
      </div>
    );
  }
  if (!apiAvailable) {
    return (
      <div className="space-y-6">
        <OutreachMessageList messages={oneOffMessages} formatDate={formatDate} />
        {oneOffMessages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-lg font-medium text-[#0A1A2F] mb-2">Interaction Tracking Coming Soon</p>
            <p className="text-sm text-[#5C6B7A] max-w-md">Interaction tracking will be available once the backend API is deployed.</p>
          </div>
        )}
      </div>
    );
  }
  if (allPlaybooks.length === 0 && oneOffMessages.length === 0 && scheduledUpcoming.length === 0) {
    return (
      <div className="text-center py-12 text-[#5C6B7A]">
        <p className="text-lg font-medium mb-2">Not Currently Engaged</p>
        <p className="text-sm">This persona is not enrolled in any playbooks and has no outreach history.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showCurrentlyEngaged && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg font-semibold text-green-700">🟢 Currently Engaged</span>
        </div>
      )}
      {allPlaybooks.map((pb) => <PlaybookCard key={pb.sequence_id} pb={pb} formatDate={formatDate} />)}

      {scheduledUpcoming.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-[#0A1A2F] mb-3">🔵 Scheduled — pending ({scheduledUpcoming.length})</h3>
          <div className="space-y-2">
            {scheduledUpcoming.map((s) => (
              <div key={s.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {isLinkedInOutboxChannel(s)
                      ? <Linkedin className="w-4 h-4 text-blue-700 flex-shrink-0" />
                      : <Mail className="w-4 h-4 text-blue-700 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A1A2F] truncate">{getOutboxDraftHeadline(s)}</p>
                      <p className="text-xs text-blue-800 mt-0.5">
                        Scheduled for {formatDate(s.scheduled_send_at)} · {new Date(s.scheduled_send_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="text-xs bg-white">{s.source === 'ad_hoc' ? 'Ad-hoc' : `Playbook · T${s.stage_no ?? '?'}`}</Badge>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">{s.channel || 'email'}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <OutreachMessageList messages={oneOffMessages} formatDate={formatDate} />
    </div>
  );
}
